import { writable, derived, get } from 'svelte/store';
import { TrackerAPI, type Tracker } from '$lib/api/trackers';
import { getQueryClient } from '$lib/query/client';
import { invalidateTrackerQueries } from '$lib/features/trackers/logic/trackerCacheInvalidation';

// Types for extended tracker states
export interface TrackerWithState extends Tracker {
	isLoading?: boolean;
	error?: string | null;
	lastAction?: 'archive' | 'trash' | 'restore' | 'unarchive' | null;
}

export interface TrackerStoreState {
	trackers: TrackerWithState[];
	isLoading: boolean;
	error: string | null;
	lastFetch: Date | null;
}

export class StaleTrackerSessionError extends Error {
	constructor() {
		super('Tracker operation belongs to a previous session');
		this.name = 'StaleTrackerSessionError';
	}
}

export function isStaleTrackerSessionError(error: unknown): error is StaleTrackerSessionError {
	return error instanceof StaleTrackerSessionError;
}

// Create the main in-memory tracker store. Tenant data must never persist in browser storage.
function createTrackerStore() {
	let sessionGeneration = 0;
	const initialState: TrackerStoreState = {
		trackers: [],
		isLoading: false,
		error: null,
		lastFetch: null
	};

	const { subscribe, set, update } = writable<TrackerStoreState>(initialState);

	// Helper function to find tracker index
	const findTrackerIndex = (trackerId: string, trackers: TrackerWithState[]) =>
		trackers.findIndex((t) => t.id === trackerId);

	// Helper function for optimistic updates with rollback
	async function optimisticUpdate<T>(
		trackerId: string,
		optimisticChange: (tracker: TrackerWithState) => TrackerWithState,
		apiCall: () => Promise<T>,
		rollback?: (tracker: TrackerWithState) => TrackerWithState
	): Promise<T> {
		const requestGeneration = sessionGeneration;
		let originalTracker: TrackerWithState | null = null;

		// Apply optimistic update
		update((state) => {
			const index = findTrackerIndex(trackerId, state.trackers);
			if (index !== -1) {
				originalTracker = { ...state.trackers[index] };
				state.trackers[index] = optimisticChange(state.trackers[index]);
			}
			return { ...state };
		});

		try {
			// Execute API call
			const result = await apiCall();
			if (requestGeneration !== sessionGeneration) throw new StaleTrackerSessionError();

			// Clear loading state on success
			update((state) => {
				const index = findTrackerIndex(trackerId, state.trackers);
				if (index !== -1) {
					state.trackers[index].isLoading = false;
					state.trackers[index].error = null;
				}
				return { ...state };
			});
			invalidateTrackerQueries(getQueryClient(), trackerId);

			return result;
		} catch (error) {
			if (requestGeneration !== sessionGeneration) throw new StaleTrackerSessionError();
			// Rollback on error
			update((state) => {
				const index = findTrackerIndex(trackerId, state.trackers);
				if (index !== -1 && originalTracker) {
					if (rollback) {
						state.trackers[index] = rollback(originalTracker);
					} else {
						state.trackers[index] = originalTracker;
					}
					state.trackers[index].error = error instanceof Error ? error.message : 'Operation failed';
				}
				state.error = error instanceof Error ? error.message : 'Operation failed';
				return { ...state };
			});
			throw error;
		}
	}

	return {
		subscribe,

		// Load all trackers
		async loadTrackers(includeArchived = false, includeTrashed = false, forceRefresh = false) {
			const requestGeneration = sessionGeneration;
			// Check if we have cached data and it's fresh (unless force refresh)
			const currentState = get({ subscribe });
			if (!forceRefresh && currentState.trackers.length > 0 && currentState.lastFetch) {
				const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
				if (currentState.lastFetch > fiveMinutesAgo) {
					console.log('Using cached tracker data');
					return currentState.trackers;
				}
			}

			update((state) => ({ ...state, isLoading: true, error: null }));

			try {
				// Load active trackers
				const response = await TrackerAPI.getTrackers();
				let allTrackers: TrackerWithState[] = response.items;

				// Optionally load archived trackers
				if (includeArchived) {
					const archivedResponse = await TrackerAPI.getArchivedTrackers();
					allTrackers = [...allTrackers, ...archivedResponse.items];
				}

				// Optionally load trashed trackers
				if (includeTrashed) {
					const trashedResponse = await TrackerAPI.getTrashedTrackers();
					allTrackers = [...allTrackers, ...trashedResponse.items];
				}
				if (requestGeneration !== sessionGeneration) return allTrackers;

				const newState = {
					trackers: allTrackers,
					isLoading: false,
					error: null,
					lastFetch: new Date()
				};

				set(newState);

				return allTrackers;
			} catch (error) {
				if (requestGeneration !== sessionGeneration) return get({ subscribe }).trackers;
				update((state) => ({
					...state,
					isLoading: false,
					error: error instanceof Error ? error.message : 'Failed to load trackers'
				}));
				throw error;
			}
		},

		// Add a new tracker (at the beginning of the list)
		addTracker(tracker: Tracker) {
			update((state) => ({
				...state,
				trackers: [tracker, ...state.trackers]
			}));
		},

		// Update an existing tracker
		updateTracker(trackerId: string, updates: Partial<Tracker>) {
			update((state) => {
				const index = findTrackerIndex(trackerId, state.trackers);
				if (index !== -1) {
					state.trackers[index] = { ...state.trackers[index], ...updates };
				}
				return { ...state };
			});
		},

		// Archive a tracker with optimistic update
		async archiveTracker(trackerId: string) {
			return optimisticUpdate(
				trackerId,
				(tracker) => ({
					...tracker,
					archived_at: new Date().toISOString(),
					isLoading: true,
					lastAction: 'archive'
				}),
				() => TrackerAPI.archiveTracker(trackerId),
				(tracker) => ({
					...tracker,
					archived_at: undefined,
					error: 'Failed to archive tracker'
				})
			);
		},

		// Unarchive a tracker with optimistic update
		async unarchiveTracker(trackerId: string) {
			return optimisticUpdate(
				trackerId,
				(tracker) => ({
					...tracker,
					archived_at: undefined,
					isLoading: true,
					lastAction: 'unarchive'
				}),
				() => TrackerAPI.unarchiveTracker(trackerId),
				(tracker) => ({
					...tracker,
					archived_at: new Date().toISOString(),
					error: 'Failed to unarchive tracker'
				})
			);
		},

		// Move tracker to trash with optimistic update
		async trashTracker(trackerId: string) {
			return optimisticUpdate(
				trackerId,
				(tracker) => ({
					...tracker,
					deleted_at: new Date().toISOString(),
					deletion_scheduled_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
					isLoading: true,
					lastAction: 'trash'
				}),
				() => TrackerAPI.trashTracker(trackerId),
				(tracker) => ({
					...tracker,
					deleted_at: undefined,
					deletion_scheduled_at: undefined,
					error: 'Failed to trash tracker'
				})
			);
		},

		// Restore tracker from trash with optimistic update
		async restoreTracker(trackerId: string) {
			return optimisticUpdate(
				trackerId,
				(tracker) => ({
					...tracker,
					deleted_at: undefined,
					deletion_scheduled_at: undefined,
					isLoading: true,
					lastAction: 'restore'
				}),
				() => TrackerAPI.restoreTracker(trackerId),
				(tracker) => ({
					...tracker,
					deleted_at: new Date().toISOString(),
					error: 'Failed to restore tracker'
				})
			);
		},

		// Permanently delete a tracker
		async permanentlyDeleteTracker(trackerId: string) {
			const requestGeneration = sessionGeneration;
			update((state) => {
				const index = findTrackerIndex(trackerId, state.trackers);
				if (index !== -1) {
					state.trackers[index].isLoading = true;
				}
				return { ...state };
			});

			try {
				await TrackerAPI.permanentlyDeleteTracker(trackerId);
				if (requestGeneration !== sessionGeneration) throw new StaleTrackerSessionError();

				// Remove from store on success
				update((state) => ({
					...state,
					trackers: state.trackers.filter((t) => t.id !== trackerId)
				}));
				invalidateTrackerQueries(getQueryClient(), trackerId);
			} catch (error) {
				if (requestGeneration !== sessionGeneration) throw new StaleTrackerSessionError();
				update((state) => {
					const index = findTrackerIndex(trackerId, state.trackers);
					if (index !== -1) {
						state.trackers[index].isLoading = false;
						state.trackers[index].error =
							error instanceof Error ? error.message : 'Failed to delete';
					}
					state.error = error instanceof Error ? error.message : 'Failed to delete tracker';
					return { ...state };
				});
				throw error;
			}
		},

		// Empty trash (delete all trashed trackers)
		async emptyTrash() {
			const requestGeneration = sessionGeneration;
			update((state) => ({ ...state, isLoading: true }));

			try {
				await TrackerAPI.emptyTrash();
				if (requestGeneration !== sessionGeneration) throw new StaleTrackerSessionError();

				// Remove all trashed trackers from store
				update((state) => ({
					...state,
					trackers: state.trackers.filter((t) => {
						const tracker = t as any;
						return !tracker.deleted_at;
					}),
					isLoading: false,
					error: null
				}));
				invalidateTrackerQueries(getQueryClient());
			} catch (error) {
				if (requestGeneration !== sessionGeneration) throw new StaleTrackerSessionError();
				update((state) => ({
					...state,
					isLoading: false,
					error: error instanceof Error ? error.message : 'Failed to empty trash'
				}));
				throw error;
			}
		},

		// Clear error state
		clearError() {
			update((state) => ({ ...state, error: null }));
		},

		// Reset store and clear cache
		reset() {
			sessionGeneration += 1;
			try {
				if (typeof localStorage !== 'undefined') localStorage.removeItem('tracker_store_state');
			} catch {
				// Storage cleanup is best effort; the in-memory boundary still resets synchronously.
			}
			set({
				trackers: [],
				isLoading: false,
				error: null,
				lastFetch: null
			});
		},

		// Force refresh from server
		async refresh() {
			return this.loadTrackers(false, false, true);
		}
	};
}

// Create the singleton store instance
export const trackerStore = createTrackerStore();

// Derived stores for filtered views
export const activeTrackers = derived(trackerStore, ($store) =>
	$store.trackers.filter((t) => {
		const tracker = t as any;
		return !tracker.archived_at && !tracker.deleted_at;
	})
);

export const archivedTrackers = derived(trackerStore, ($store) =>
	$store.trackers.filter((t) => {
		const tracker = t as any;
		return tracker.archived_at && !tracker.deleted_at;
	})
);

export const trashedTrackers = derived(trackerStore, ($store) =>
	$store.trackers.filter((t) => {
		const tracker = t as any;
		return tracker.deleted_at;
	})
);

// Utility function to check if a tracker is available for use
export function isTrackerAvailable(tracker: Tracker | TrackerWithState): boolean {
	// Check if the tracker has soft-delete fields (they may not be in the type but exist at runtime)
	const t = tracker as any;
	return !t.archived_at && !t.deleted_at;
}

// Utility function to filter out unavailable trackers
export function filterAvailableTrackers(
	trackers: (Tracker | TrackerWithState)[]
): (Tracker | TrackerWithState)[] {
	return trackers.filter(isTrackerAvailable);
}

// Get tracker by ID from store
export function getTrackerById(trackerId: string): TrackerWithState | undefined {
	const state = get(trackerStore);
	return state.trackers.find((t) => t.id === trackerId);
}

// Check if store needs refresh (older than 5 minutes)
export function shouldRefreshTrackers(): boolean {
	const state = get(trackerStore);
	if (!state.lastFetch) return true;

	const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
	return state.lastFetch < fiveMinutesAgo;
}
